import { Injectable } from '@angular/core';
import { from, of } from 'rxjs';
import { catchError, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { PasskeysService } from '@api/passkeys.service';
import { PasskeyDTO } from '@dto/gateway-secured/passkeys/models';
import { BaseObject } from '@shared/base/base-object';
import { FeatureFlagsService } from '@shared/feature-flags/feature-flags.service';
import { AuthService } from '@shared/helpers/auth.service';
import { PasskeyBrowserService } from '@shared/helpers/passkey-browser.service';
import { TranslateService } from '@shared/translates/translate.service';
import { DialogService } from '@ui/dialog/dialog.service';
import { PasskeyOfferDialogFactory } from 'app/dialogs/passkey-offer/passkey-offer-dialog.factory';
import {
  PasskeyOfferDialogData,
  PasskeyOfferDialogResult,
} from 'app/dialogs/passkey-offer/passkey-offer-dialog.types';
import { NotificationsService } from 'app/notifications/notifications.service';
import { ToastType } from 'app/notifications/notifications.types';
import {
  clearPasskeyOnThisDevice,
  declinePasskeyOfferForever,
  hasPasskeyOnThisDevice,
  isPasskeyOfferDeclined,
  isPasskeyOfferSeen,
  markPasskeyOfferSeen,
  passkeyMarkerUserId,
} from './passkey-offer-markers';

/**
 * How recently the user must have authenticated for this page load to count as "they have just
 * signed in", in seconds.
 *
 * Two minutes, and it is sized for one job: covering the gap between the gateway issuing the token
 * and the shell of the reloaded page asking the question. That gap is a redirect plus a cold
 * Angular boot — a second or two normally, and tens of seconds on a bad connection or a cold
 * cache — so the window has to be comfortably wider than the worst of those, and no wider.
 *
 * Deliberately NOT the tenant's `passkey.reauth.window.minutes`. That window answers a different
 * question — may this session register a passkey without re-entering a password (accounts answers
 * 1004 outside it) — and is measured in tens of minutes. Reusing it here would put the dialog in
 * front of anyone who merely opens a tab within it, which is one of the two defects this gate
 * exists to prevent.
 */
const FRESH_LOGIN_WINDOW_SEC = 120;

/**
 * Decides whether to offer "save a passkey on this device?" after a login, and shows the dialog.
 *
 * Called from the shell (`LayoutComponent`), because password, 2FA, passkey and SSO all arrive
 * there and only there — an SSO login never touches `/login` at all. But the shell mounts on every
 * cold load of an authorised page, which is NOT a login, and telling the two apart is the entire
 * difficulty. Hanging the offer off the mount put it in front of anyone opening the app in a new
 * tab, and worse: such a session is long past accounts' freshness window, so it answers 1004 and
 * the dialog asks for a password — reproducing the unprompted password request this whole feature
 * exists to remove.
 *
 * The answer cannot be kept in memory, which is how the first attempt at this gate failed the other
 * way and stopped the offer appearing at all. Every login path ends in
 * `RerdirectService.goToPostLoginPage()` — `location.replace`, a full page load — so the shell that
 * asks belongs to a different Angular than the one that signed the user in. A flag set during the
 * login is always false by the time it is read.
 *
 * What does survive the reload is the access token, and `AuthService.authenticationTime()` reads
 * its `auth_time` claim: when the user actually authenticated, straight from the gateway, immune to
 * both reloads and object lifetimes. A token refresh does not disturb it, because the claim
 * describes the authentication and not the token. Pairing it with a `sessionStorage` marker keyed by
 * that same `auth_time` gives the missing half — reload the page as often as you like and the offer
 * is made once, while a tab opened later has no such key and an `auth_time` too old to qualify.
 * With no claim at all (an older gateway, or a rollback) the offer is not made: degrade into
 * silence, never into nagging.
 *
 * The conditions are then checked in order of what they cost, and the order is the point:
 *  1. `supported` and `platformAuthenticatorAvailable()` — free, and false on any browser or
 *     machine that could not keep a passkey anyway;
 *  2. the token claim and the per-user localStorage markers — also free, and the reason a user who
 *     already has a key here, or who said "don't ask again", costs the login path no request at all;
 *  3. `GET /passkeys` — one request, once, and only for a user who might actually say yes.
 *
 * Preview flag first, before any marker is written: off trial/lwam/pp the Passkeys tab is hidden,
 * so a user who accepted here could never see, rename or delete what they saved. Leaving the
 * "seen" marker unwritten also means promoting `passkey` to `'ga'` still offers on the next login.
 *
 * Nothing here can break a session that has just been established: every failure ends in silence.
 */
@Injectable({
  providedIn: 'root',
})
export class PasskeyOfferService extends BaseObject {
  /**
   * The shell calls `offerIfNeeded` again on every NavigationEnd; the one-shot marker is written by
   * the first of those, and this stops a second check being started before it is.
   */
  private asked = false;

  constructor(
    private auth: AuthService,
    private passkeyBrowser: PasskeyBrowserService,
    private passkeysService: PasskeysService,
    private dialogService: DialogService,
    private notificationsService: NotificationsService,
    private translateService: TranslateService,
    private featureFlags: FeatureFlagsService,
  ) {
    super();
  }

  public offerIfNeeded(): void {
    if (this.asked || !this.auth.loggedIn || !this.passkeyBrowser.supported) {
      return;
    }

    if (!this.featureFlags.isEnabled('passkey')) {
      return;
    }

    const userId = passkeyMarkerUserId();

    if (!userId) {
      return;
    }

    const authTime = this.auth.authenticationTime();

    // No claim to read: an older gateway, or a rollback of one. Say nothing rather than guess.
    if (!authTime) {
      return;
    }

    // A clock a little behind the gateway's makes this negative, and that still means "just now";
    // only an authentication older than the window is disqualified.
    if (Date.now() / 1000 - authTime > FRESH_LOGIN_WINDOW_SEC) {
      return;
    }

    // Last, and written before the asynchronous part starts, exactly as the one-shot flag used to be
    // spent there: nothing above may burn it on a check that then bails, and nothing below may run
    // twice for one authentication however many times the page is reloaded.
    if (isPasskeyOfferSeen(userId, authTime)) {
      return;
    }

    this.asked = true;
    markPasskeyOfferSeen(userId, authTime);

    from(this.passkeyBrowser.platformAuthenticatorAvailable())
      .pipe(
        switchMap((available) => {
          if (!available || isPasskeyOfferDeclined(userId) || hasPasskeyOnThisDevice(userId)) {
            return of(null as PasskeyDTO[]);
          }

          return this.passkeysService
            .list()
            .pipe(
              map((res): PasskeyDTO[] =>
                res.success && Array.isArray(res.data) ? res.data : null,
              ),
            );
        }),
        // A capability check that rejects, or an unreachable server, is not something to report:
        // the user did not ask for this dialog and is in the middle of arriving somewhere.
        catchError(() => of(null as PasskeyDTO[])),
        takeUntil(this.destroy$),
      )
      .subscribe((passkeys) => {
        if (passkeys?.length === 0) {
          // The account has none, so this browser certainly has none either: the authoritative
          // answer, and the backstop for a stale "there is a passkey here" marker.
          clearPasskeyOnThisDevice(userId);
          this.openDialog(userId);
        }
      });
  }

  private openDialog(userId: string): void {
    this.dialogService
      .openLazy<PasskeyOfferDialogData, PasskeyOfferDialogResult>(
        { factory: PasskeyOfferDialogFactory },
        null,
        { ...this.dialogService.RIGHT_SIDE_DIALOG_CONFIG, width: '480px' },
      )
      .closed$.pipe(take(1), takeUntil(this.destroy$))
      .subscribe((result) => this.remember(userId, result));
  }

  /**
   * "Not now" and a dismissed dialog record nothing on purpose: the offer is worth repeating on a
   * later session, and only an explicit "don't ask again" is permanent.
   */
  private remember(userId: string, result: PasskeyOfferDialogResult): void {
    if (result?.outcome === 'registered') {
      // The marker itself is written by `PasskeyRegistrationService`, so a passkey added from User
      // Settings counts too; here there is only the confirmation to show.
      this.notificationsService.openToast(ToastType.Success, {
        title: this.translateService.get('dialogs.user-settings.passkeys.created'),
      });

      return;
    }

    if (result?.outcome === 'never') {
      declinePasskeyOfferForever(userId);
    }
  }
}
